import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Search, Sparkles } from 'lucide-react';
import { apiClient, type SuggestionSource } from '../../api/client';
import { IntegrationIcon } from '../IntegrationIcon';
import { filterSources, toggleSource } from './simulatedSuggestionSelection';

interface SimulatedSuggestionsModalProps {
  open: boolean;
  onClose: () => void;
  /** Receives the chosen source keys once the customer confirms. */
  onGenerate: (sources: string[]) => void;
}

/**
 * Integration picker for simulated workflow suggestions.
 *
 * A customer with nothing connected has no basis for real suggestions, so they
 * choose which integrations to explore and Kestrel generates suggestions as if
 * those were connected. The selection is capped server-side (each source is an
 * independent LLM call); the cap is fetched rather than hardcoded so the copy
 * cannot drift from what the backend enforces.
 */
export function SimulatedSuggestionsModal({
  open,
  onClose,
  onGenerate,
}: SimulatedSuggestionsModalProps) {
  const [sources, setSources] = useState<SuggestionSource[]>([]);
  const [maxSources, setMaxSources] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiClient
      .getSuggestionSources()
      .then(res => {
        if (cancelled) return;
        setSources(res.sources || []);
        if (res.max_sources > 0) setMaxSources(res.max_sources);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the list of integrations. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  // Reset the selection each time the modal opens so a dismissed run doesn't
  // silently carry over.
  useEffect(() => {
    if (open) setSearch('');
    else setSelected([]);
  }, [open]);

  const filtered = useMemo(() => filterSources(sources, search), [sources, search]);

  const atCap = selected.length >= maxSources;

  const toggle = (source: string) => {
    setSelected(prev => toggleSource(prev, source, maxSources));
  };

  const handleGenerate = () => {
    if (selected.length === 0) return;
    onGenerate(selected);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Generate simulated suggestions</DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Pick the integrations you want to see workflows for. Kestrel will generate
            suggestions as if they were connected, so you can explore what it would
            automate before setting anything up. Select up to {maxSources}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 min-w-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search integrations..."
              className="h-8 pl-8 text-xs"
              autoFocus
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading integrations...
            </div>
          ) : loadError ? (
            <p className="py-10 text-center text-xs text-destructive">{loadError}</p>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-md border border-border p-1 space-y-0.5">
              {filtered.map(src => {
                const isSelected = selected.includes(src.source);
                const disabled = !isSelected && atCap;
                return (
                  <label
                    key={src.source}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs ${
                      disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent cursor-pointer'
                    }`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(src.source)}
                      disabled={disabled}
                      className="h-3.5 w-3.5"
                    />
                    <IntegrationIcon integration={src.icon} size={14} />
                    <span className="min-w-0 truncate flex-1">{src.label}</span>
                  </label>
                );
              })}
              {filtered.length === 0 && (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">No integrations match.</p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 min-w-0">
            <p className="text-[11px] text-muted-foreground truncate">
              {selected.length} of {maxSources} selected
              {atCap && ' — limit reached'}
            </p>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => setSelected([])}
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                Clear
              </button>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Simulated suggestions are tagged in the Library and open in the builder's
            simulated mode. They can be edited and tested, but not activated until the
            integration is connected.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleGenerate} disabled={selected.length === 0} className="gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
